import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Drawer,
  Typography,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import type { TeamUserTableData, CreateTeamUserRequest, TeamUserRole } from '../types';
import { teamUserService, type Role } from '@/services/teamUserService';

interface TeamUserFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateTeamUserRequest & { role_uuid?: string }) => Promise<void>;
  teamUser?: TeamUserTableData | null;
  isLoading?: boolean;
}

export const TeamUserForm: React.FC<TeamUserFormProps> = ({
  open,
  onClose,
  onSubmit,
  teamUser,
  isLoading = false,
}) => {

  // ✅ Safe initialization (avoids null reference)
  const [formData, setFormData] = useState<CreateTeamUserRequest>({
    name: teamUser?.name || '',
    email: teamUser?.email || '',
    password: '',
  });

  const [selectedRole, setSelectedRole] = useState<string>('');
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string>('');

  // Load roles on component mount
  useEffect(() => {
    if (open) {
      loadRoles();
    }
  }, [open]);

  // ✅ Reinitialize form when editing a different user
  useEffect(() => {
    setFormData({
      name: teamUser?.name || '',
      email: teamUser?.email || '',
      password: '',
    });
    setErrors({});
    setApiError('');
  }, [teamUser]);

  // ✅ Set selected role when roles are loaded and teamUser is available
  useEffect(() => {
    if (teamUser && roles.length > 0) {
      // Find the role UUID by matching role_name (most reliable)
      const matchingRole = roles.find(role => 
        role.role_name === teamUser.role_name
      );
      if (matchingRole) {
        setSelectedRole(matchingRole.uuid);
      } else {
        setSelectedRole('');
      }
    } else if (!teamUser) {
      // Reset role selection when not editing
      setSelectedRole('');
    }
  }, [teamUser, roles]);

  const loadRoles = async () => {
    setIsLoadingRoles(true);
    try {
      const rolesData = await teamUserService.getRoles();
      setRoles(rolesData);
    } catch (error) {
      console.error('Error loading roles:', error);
      setApiError('Failed to load roles');
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const handleInputChange = (field: keyof CreateTeamUserRequest) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleRoleChange = (event: any) => {
    setSelectedRole(event.target.value);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!teamUser && !formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData.password && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!selectedRole) {
      newErrors.role = 'Role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) return;

    try {
      await onSubmit({
        ...formData,
        password: formData.password || 'defaultPassword123', // Default password for updates
        role_uuid: selectedRole,
      });

      // Reset form after success
      setFormData({
        name: '',
        email: '',
        password: '',
      });
      setSelectedRole('');
      setErrors({});
      setApiError('');
    } catch (error: any) {
      console.error('Form submission error:', error);
      // Show API error message
      const errorMessage = error?.response?.data?.message || error?.message || 'An error occurred while saving the user';
      setApiError(errorMessage);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
    });
    setSelectedRole('');
    setErrors({});
    setApiError('');
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 400,
          padding: 0,
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" component="h2">
              {teamUser ? 'Edit Team User' : 'Add New Team User'}
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Form Content */}
        <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </Alert>
          )}
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Name"
                value={formData.name}
                onChange={handleInputChange('name')}
                error={!!errors.name}
                helperText={errors.name}
                fullWidth
                size="small"
                required
              />

              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                error={!!errors.email}
                helperText={errors.email}
                fullWidth
                size="small"
                required
              />

              <TextField
                label="Password"
                type="password"
                value={formData.password}
                onChange={handleInputChange('password')}
                error={!!errors.password}
                helperText={
                  errors.password || (teamUser ? 'Leave blank to keep current password' : '')
                }
                fullWidth
                size="small"
                required={!teamUser}
              />

              <FormControl
                fullWidth
                error={!!errors.role}
                disabled={isLoadingRoles}
              >
                <InputLabel>Role *</InputLabel>
                <Select
                  value={selectedRole}
                  onChange={handleRoleChange}
                  label="Role *"
                  size="small"
                >
                  {isLoadingRoles ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Loading roles...
                    </MenuItem>
                  ) : (
                    roles.map((role) => (
                      <MenuItem key={role.uuid} value={role.uuid}>
                        {role.role_name}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {errors.role && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {errors.role}
                  </Typography>
                )}
              </FormControl>
            </Box>
          </form>
        </Box>

        {/* Footer Actions */}
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : null}
              onClick={handleSubmit}
            >
              {isLoading ? 'Saving...' : teamUser ? 'Update' : 'Create'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};
